import { Link } from "react-router-dom";
import {
  Shield, Activity, MessageCircleQuestion, FileSearch, Settings,
  Coins, Sparkles, Newspaper, Radio, Hash, ArrowRight, MicVocal,
} from "lucide-react";

/**
 * The Newseze Difference — value-prop landing band.
 * Sits on Home above the main grid (under the trust strip / Near You).
 * Each tile links directly to the live feature it advertises so the band
 * doubles as a navigation/discovery surface for new visitors.
 */
export default function NewsezeDifference() {
  return (
    <section className="py-8 border-y-2 border-[var(--gold)] bg-gradient-to-br from-[#0a1f3d] via-[#0e2d63] to-[#1e3a8a] text-white -mx-[max(0px,calc((100vw-1280px)/2+20px))] px-[max(20px,calc((100vw-1280px)/2+20px))]" data-testid="newseze-difference">
      <div className="max-w-[1280px] mx-auto">

        {/* Heading */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-7">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-black text-[var(--gold)] mb-1.5 flex items-center gap-2">
              <Sparkles size={12} /> The Newseze Difference
            </div>
            <h2 className="text-[28px] sm:text-[36px] lg:text-[44px] font-black tracking-[-0.025em] leading-[1.05]" data-testid="newseze-difference-h2">
              Interactive news with calm,<br className="hidden sm:block" /> factual views.
            </h2>
            <p className="text-[13.5px] sm:text-[15px] text-white/80 mt-3 max-w-[640px] leading-snug">
              No drama. No conspiracies. A balanced, trust-ranked newsroom you can ask questions, customize,
              and earn from — built so you stay informed without being exhausted.
            </p>
          </div>
          <Link to="/about" className="hidden sm:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)] hover:text-white transition" data-testid="newseze-difference-learn">
            Why we built this <ArrowRight size={12} />
          </Link>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-7">
          <FeatureTile
            icon={Shield}
            kicker="Calm filter"
            title="No drama. No conspiracies."
            body="Every story passes evidence, respect, drama, and conspiracy filters before it ever reaches your feed."
            href="/trust"
            testid="diff-calm"
          />
          <FeatureTile
            icon={Newspaper}
            kicker="Trust-ranked"
            title="Balanced &amp; trusted responses."
            body="Each story carries a public trust score. Read the same news from across the spectrum, side by side."
            href="/trust"
            testid="diff-trust"
          />
          <FeatureTile
            icon={Settings}
            kicker="Your interests, our business"
            title="Customized news feeds."
            body="Tell Newseze your subjects, regions, blocked topics, and outlets to avoid. Saved across devices."
            href="/reader-tools"
            testid="diff-custom"
          />
          <FeatureTile
            icon={FileSearch}
            kicker="My Investigator"
            title="Investigate any topic, with ongoing tracking."
            body="Submit a subject. Newseze writes the investigation — timeline, pictures, related video, fresh updates every hour."
            href="/reader-tools"
            testid="diff-investigator"
            gold
          />
          <FeatureTile
            icon={MessageCircleQuestion}
            kicker="Questeze"
            title="Ask Chella, Michael, or Marcus &mdash; immediately."
            body="Got a question about a story? Ask the assigned Newseze reporter. They answer in their own voice, grounded in our reporting — no speculation, no drama."
            href="/newsroom"
            testid="diff-ask"
            gold
          />
          <FeatureTile
            icon={Coins}
            kicker="Rewards"
            title="Get rewarded to learn."
            body="Earn credits as you read, watch, and listen. Redeem for member benefits, gift cards, or charity."
            href="/rewards"
            testid="diff-rewards"
          />
        </div>

        {/* "Pick any" interactive panel */}
        <div className="rounded-2xl border border-[var(--gold)] bg-white/5 backdrop-blur-sm p-5 sm:p-6" data-testid="diff-pick-panel">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-1.5">
            <Sparkles size={12} /> Customize your news · access anyone
          </div>
          <h3 className="text-[20px] sm:text-[24px] font-black tracking-[-0.02em] leading-tight mb-1">
            Three ways in. One calm voice out.
          </h3>
          <p className="text-[13px] text-white/75 mb-5 max-w-[680px] leading-snug">
            Pick any topic, paste any story, or follow any outlet — Newseze delivers an analysis, a trust score,
            and a calm response in the Newseze voice.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <PickTile
              num="1"
              icon={Hash}
              label="Pick a topic"
              cta="Build a custom feed"
              href="/reader-tools?tab=custom"
              testid="pick-topic"
            />
            <PickTile
              num="2"
              icon={Newspaper}
              label="Paste anything"
              cta="Score any URL — news, X, YouTube, Reddit"
              href="/reader-tools?tab=paste"
              testid="pick-story"
            />
            <PickTile
              num="3"
              icon={Radio}
              label="Pick a station"
              cta="Explore Networks"
              href="/networks"
              testid="pick-station"
            />
          </div>

          {/* Daily / Instant brief secondary CTA */}
          <div className="mt-5 pt-4 border-t border-white/15 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2.5">
              <MicVocal size={18} className="text-[var(--gold)]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)]">Daily &amp; instant briefs</div>
                <div className="text-[13.5px] font-bold">A 90-second read of everything that mattered today.</div>
              </div>
            </div>
            <Link to="/?brief=daily" className="btn gold" data-testid="diff-daily-brief">
              Get my brief <ArrowRight size={12} />
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}

function FeatureTile({ icon: Icon, kicker, title, body, href, testid, gold = false }) {
  return (
    <Link
      to={href}
      className={`group rounded-xl p-4 sm:p-5 border transition flex flex-col gap-2 ${
        gold
          ? "bg-[var(--gold)] text-[#1d1303] border-[var(--gold)] hover:bg-[#fff0c2]"
          : "bg-white/[0.06] backdrop-blur-sm border-white/15 text-white hover:bg-white/[0.12] hover:border-[var(--gold)]"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md ${gold ? "bg-[#1d1303] text-[var(--gold)]" : "bg-[var(--gold)] text-[#1d1303]"}`}>
          <Icon size={14} />
        </div>
        <span className={`text-[10px] uppercase tracking-[0.18em] font-black ${gold ? "text-[#7a5b00]" : "text-[var(--gold)]"}`}>{kicker}</span>
      </div>
      <div className={`text-[16px] sm:text-[18px] font-black tracking-[-0.015em] leading-snug ${gold ? "text-[#1d1303]" : "text-white"}`} dangerouslySetInnerHTML={{ __html: title }} />
      <p className={`text-[12.5px] leading-snug ${gold ? "text-[#3d2a00]" : "text-white/75"}`}>{body}</p>
      <div className={`text-[11px] uppercase tracking-[0.16em] font-black mt-1 inline-flex items-center gap-1 ${gold ? "text-[#7a5b00] group-hover:text-[#1d1303]" : "text-[var(--gold)] group-hover:text-white"}`}>
        Explore <ArrowRight size={12} />
      </div>
    </Link>
  );
}

function PickTile({ num, icon: Icon, label, cta, href, testid }) {
  return (
    <Link
      to={href}
      className="group rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] hover:border-[var(--gold)] p-4 transition flex items-center gap-3"
      data-testid={testid}
    >
      <div className="text-[40px] font-black text-[var(--gold)] tabular-nums leading-none w-8 text-center shrink-0">{num}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] mb-0.5">
          <Icon size={11} /> {label}
        </div>
        <div className="text-[13.5px] font-black text-white leading-tight group-hover:text-[var(--gold)] transition">{cta}</div>
      </div>
      <ArrowRight size={14} className="text-white/40 group-hover:text-[var(--gold)] shrink-0" />
    </Link>
  );
}
